import React, { Fragment } from 'react'

import { GetOrderedList } from '@dashboard/lib/personality'
import Button from '@dashboard/components/Button'
import classNamesBind from 'classnames/bind'
import {
  DiscType,
  CircumplexBreakdownValue,
  Profile,
} from '@dashboard/lib/types'
import { universalModulo } from '@dashboard/lib/number'

import css from './styles.module.scss'
const classNames = classNamesBind.bind(css)

const Chevron = '/static/share/images/chevron-left.svg'

interface OwnProps {
  profiles: Profile[]
  breakdown: Partial<Record<DiscType, CircumplexBreakdownValue>>
  breakdownArrowBehavior?: 'next_type' | 'next_profile'
  selectedType: DiscType | null
  selectType: (type: DiscType) => void
  breakdownInteraction?: () => void
}

interface State {
  index: number
}

export default class DiscChart extends React.Component<OwnProps, State> {
  // Constructor
  constructor(props: OwnProps) {
    super(props)

    this.state = {
      index: 0,
    }
  }

  componentDidMount() {
    const { selectType, breakdown, profiles } = this.props
    const { index } = this.state
    const keys = this.getSortedKeys()
    const firstType = keys.find(discType =>
      profiles.find(p => p.personality?.disc_type === discType),
    )
    const firstBreakdown = firstType ? breakdown[firstType] : null

    if (firstBreakdown) selectType(firstBreakdown.disc_type)
    else selectType(keys[index])
  }

  componentDidUpdate(prevProps: OwnProps, prevState: State) {
    const { breakdown, selectedType, selectType } = this.props
    const { index } = this.state
    const keys = this.getSortedKeys()
    const prevKeys = this.getSortedKeys(prevProps)
    const breakdownChanged = prevKeys.join('-') !== keys.join('-')

    const typeChanged = prevProps.selectedType !== selectedType

    if (typeChanged || breakdownChanged) {
      if (selectedType !== null && breakdown[selectedType]) {
        const index = keys.indexOf(selectedType)
        this.setState({ index })

        selectType(keys[index])
      } else {
        this.setState({ index: 0 })

        if (keys.length) selectType(keys[0])
      }
    }

    const navigated = prevState.index !== index
    if (navigated) {
      selectType(keys[index])
    }
  }

  // Render
  render() {
    const { breakdown, profiles } = this.props
    const { index } = this.state
    const key = this.getSortedKeys()[index]
    const breakdownKeys = Object.keys(breakdown).filter(type =>
      profiles.some(p => p.personality?.disc_type == type),
    ) as DiscType[]

    return (
      <div className={classNames('container')}>
        <div className={classNames('no-print')}>
          <div key={key} className={classNames('breakdown')}>
            {this.renderHeader(key)}
            {this.renderContent(key)}
          </div>
        </div>
        <div className={classNames('print-only')}>
          {breakdownKeys.map(key => {
            return (
              <div key={key} className={classNames('breakdown')}>
                {this.renderHeader(key)}
                {this.renderContent(key)}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  renderHeader(key: DiscType) {
    const { breakdown } = this.props
    const breakdownObj = breakdown[key]
    const primaryType =
      breakdownObj && breakdownObj.disc_type ? breakdownObj.disc_type : ''
    const archetypeType = breakdownObj && breakdownObj.archetype

    if (Object.keys(breakdown).length > 1) {
      return (
        <div className={classNames('header')}>
          <img
            className={classNames('chevron')}
            onClick={() => this.navigate('back')}
            src={Chevron}
          />
          <h3 className={classNames(primaryType)}>
            {archetypeType ? `${archetypeType}s` : ''}
          </h3>
          <img
            className={classNames('chevron', 'forward')}
            onClick={() => this.navigate('forward')}
            src={Chevron}
          />
        </div>
      )
    } else
      return (
        <div className={classNames('header')}>
          <h3 className={classNames(primaryType)}>{archetypeType}</h3>
        </div>
      )
  }

  renderContent(key: DiscType) {
    const { breakdown } = this.props
    const archetype = breakdown[key]

    if (!archetype) return null

    return (
      <div className={classNames('content')}>
        {this.renderText(archetype)}
        {this.renderList(archetype)}
        {this.renderNextButton()}
      </div>
    )
  }

  renderText(archetype: CircumplexBreakdownValue) {
    const primaryType =
      archetype && archetype.disc_type ? archetype.disc_type : ''
    const overview = archetype.overview.replace('like This group', '')

    return (
      <Fragment>
        <span className={classNames('span', primaryType)}>
          {archetype.archetype}s{' '}
        </span>
        {overview.substr(archetype.overview.indexOf(' ') + 1)}
      </Fragment>
    )
  }

  renderList(archetype: CircumplexBreakdownValue) {
    const primaryType = archetype.disc_type

    if (!archetype.strengths) return null

    return (
      <div className={classNames('list')}>
        <div>
          In groups,{' '}
          <span className={classNames('span', primaryType)}>
            {archetype.archetype}s
          </span>{' '}
          can be most effective:
        </div>
        <ol>
          {archetype.strengths.map(strength => {
            return <li key={strength}>{strength}</li>
          })}
        </ol>
      </div>
    )
  }

  renderNextButton() {
    const { breakdown } = this.props

    if (Object.keys(breakdown).length <= 1) return null

    return (
      <div className={classNames('centered')}>
        <Button small outline onClick={() => this.navigate('forward')}>
          Next
        </Button>
      </div>
    )
  }

  // Functions
  navigate = (direction: 'forward' | 'back') => {
    const { breakdownInteraction, breakdownArrowBehavior } = this.props
    const { index } = this.state

    breakdownInteraction && breakdownInteraction()

    const sortedKeys = this.getSortedKeys()
    const maxIndex = sortedKeys.length - 1

    if (breakdownArrowBehavior === 'next_profile') {
      this.setState({ index: this.getNextProfileIndex(direction) })
    } else {
      if (direction == 'forward') {
        if (index == maxIndex) this.setState({ index: 0 })
        else this.setState({ index: index + 1 })
      } else {
        if (index == 0) this.setState({ index: maxIndex })
        else this.setState({ index: index - 1 })
      }
    }
  }

  getNextProfileIndex(direction: 'forward' | 'back'): number {
    const { profiles } = this.props
    const { index } = this.state
    const sortedKeys = this.getSortedKeys()
    const maxIndex = sortedKeys.length - 1
    const forward = direction === 'forward'

    for (let i = 0; i < maxIndex; i++) {
      const profileIndex = forward
        ? universalModulo(i + index + 1, maxIndex + 1)
        : universalModulo(index - i - 1, maxIndex + 1)

      if (
        profiles.some(
          p => p.personality?.disc_type === sortedKeys[profileIndex],
        )
      ) {
        return profileIndex
      }
    }

    return 0
  }

  getSortedKeys = (prevProps?: OwnProps): DiscType[] => {
    const { breakdown } = prevProps || this.props

    const keys = Object.keys(breakdown) as DiscType[]

    if (keys.length == 1) return keys

    const order = GetOrderedList()

    const sortedKeys = keys.sort(function (a: DiscType, b: DiscType) {
      return order.indexOf(a) - order.indexOf(b)
    })

    return sortedKeys
  }
}
